import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { DropDownHintMsg, DropDownField, CheckboxMenu } from "../../index";
import './dropdown.scss';

export const DropDownMultiSelect = React.forwardRef(({
    autoFocus = false,
    colorStyle = 'neutral',
    disabled = false,
    disabledOptions,
    hintMsg,
    id,
    label,
    name,
    multiselectCount,
    onBlur = () => { },
    onChange = () => { },
    onDeselectAllClick = () => { },
    onSelectAllClick = () => { },
    options,
    required = false,
    selectedOptions,
    showSelectAll,
    ...inputProps
}, ref) => {

    const dropdownRef = ref;

    const getSelectedLabel = () => {
        return selectedOptions?.length > 1 ? selectedOptions?.length + ` of ${multiselectCount ? multiselectCount : options?.length} selected` : selectedOptions?.length > 0 ? selectedOptions[0].label : '';
    }

    const [selectedLabel, setSelectedLabel] = React.useState(getSelectedLabel());
    const [selectedList, setSelectedList] = React.useState(selectedOptions || []);
    const [showOptions, setShowOptions] = React.useState(false);
    const [hasValue, setHasValue] = React.useState(selectedOptions);

    useEffect(() => {
        setHasValueStyle(selectedOptions);
        setSelectedLabel(getSelectedLabel());
    }, [selectedOptions]);

    const toggleOptions = (e) => {
        setShowOptions(!showOptions);
    }

    const handleChange = (e, optValue, optLabel) => {
        let updatedList = selectedOptions;
        let index = updatedList.findIndex(opt => { return opt.value === optValue });
        let changedItem = {
            value: optValue,
            label: optLabel,
            selected: index > -1 ? false : true //if not found then set to true since being selected
        }

        if (changedItem.selected) {
            updatedList.push({ value: optValue, label: optLabel });
        } else {
            updatedList.splice(index, 1);
        }

        let selected = options.filter(item => { return updatedList.some(sOpt => sOpt.value === item.value) });
        setSelectedList(selected);

        let evt = {
            ...event, target: {
                value: selected,
                name: dropdownRef.current.name ? dropdownRef.current.name : ""
            }
        }

        setHasValueStyle(selected);
        onChange(evt, changedItem);
    }

    const handleBlur = (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setShowOptions(false);

            let evt = {
                ...event, target: {
                    value: selectedList,
                    name: dropdownRef.current.name ? dropdownRef.current.name : ""
                }
            }

            if (onBlur) {
                onBlur(evt);
            }
        }
    }

    // Event handler for keydowns
    const handleInputKeyDown = (e) => {
        switch (e.key) {
            case " ":
            case "SpaceBar":
            case "Enter":
                e.preventDefault();
                toggleOptions();
                break;
            default:
                break;
        }
    };

    // Event handler for keydowns
    const handleOptionsKeyDown = (e, value, label) => {
        switch (e.key) {
            case " ":
            case "SpaceBar":
            case "Enter":
                e.preventDefault();
                e.persist();
                handleChange(e, value, label);
                break;
            case "Escape":
                e.preventDefault();
                setShowOptions(false);
            default:
                break;
        }
    };

    const handleSelectAll = (event) => {
        // Do not select option if disabled and not selected
        let dontSelect = disabledOptions?.length > 0 
            ? selectedOptions?.length > 0 
                ? disabledOptions.filter(item => { return selectedOptions.some(sOpt => sOpt.value !== item.value) })
                : disabledOptions
            : []
        let selected = dontSelect?.length > 0
            ? options.filter(item => { return dontSelect.some(dOpt => dOpt.value !== item.value) })
            : options;
        setSelectedList(selected);

        let evt = {
            ...event, target: {
                value: selected,
                name: dropdownRef.current.name ? dropdownRef.current.name : ""
            }
        }

        setHasValueStyle(selected);
        onChange(evt, null);
        if (onSelectAllClick) {
            onSelectAllClick(event);
        }
    };

    const handleDeselectAll = (event) => {
        // Keep option selected if disabled and selected
        let keepSelected = disabledOptions?.length > 0
            ? selectedOptions?.length > 0
                ? disabledOptions.filter(item => { return selectedOptions.some(sOpt => sOpt.value === item.value) })
                : []
            : []
        let selected = keepSelected?.length > 0
            ? keepSelected
            : [];
        setSelectedList(selected);

        let evt = {
            ...event, target: {
                value: selected,
                name: dropdownRef.current.name ? dropdownRef.current.name : ""
            }
        }

        setHasValueStyle(selected);
        onChange(evt, null);
        if (onDeselectAllClick) {
            onDeselectAllClick(event);
        }
    };

    const setHasValueStyle = (options) => {
        if (options?.length > 0) {
            setHasValue(true);
        } else {
            setHasValue(false);
        }
    }

    const dropdownClasses = [
        `bsw-dropdown`,
        `bsw-dropdown-color--${colorStyle}`,
        `${disabled ? 'bsw-dropdown--disabled' : ''}`,
        `${hasValue ? 'bsw-dropdown--filled' : ''}`,
    ].join(' ');

    return (
        <div className={dropdownClasses} onBlur={handleBlur}>
            <DropDownField
                {...inputProps}
                autoFocus={autoFocus}
                disabled={disabled}
                id={id}
                label={label}
                name={name}
                onClick={toggleOptions}
                onKeyDown={handleInputKeyDown}
                ref={dropdownRef}
                required={required}
                selectedLabel={selectedLabel}
                showOptions={showOptions}
            />
            {(options && showOptions && !disabled) &&
                <CheckboxMenu
                    disabledOptions={disabledOptions}
                    id={id}
                    multiselectCount={multiselectCount}
                    onClick={handleChange}
                    onDeselectAllClick={handleDeselectAll}
                    onKeyDown={handleOptionsKeyDown}
                    onSelectAllClick={handleSelectAll}
                    options={options}
                    required={required}
                    selectedOptions={selectedOptions}
                    showSelectAll={showSelectAll}
                />
            }
            {hintMsg &&
                <DropDownHintMsg
                    colorStyle={colorStyle}
                    hintMsg={hintMsg}
                />
            }
        </div>
    )
})

DropDownMultiSelect.propTypes = {
    autoFocus: PropTypes.bool,
    colorStyle: PropTypes.string,
    disabled: PropTypes.bool,
    disabledOptions: PropTypes.array,
    hintMsg: PropTypes.string,
    id: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string,
    multiselectCount: PropTypes.number,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onDeselectAllClick: PropTypes.func,
    onSelectAllClick: PropTypes.func,
    options: PropTypes.array,
    required: PropTypes.bool,
    selectedOptions: PropTypes.array,
    showSelectAll: PropTypes.bool,
}
